import _ from 'lodash';
import { FC, KeyboardEvent, useState } from 'react';
import * as Papa from 'papaparse';
import { Box, Button } from '@platform/ui-library';
import { FileUploader } from '../../form_components';
import { createCsv } from '../../utilities';
import { Table } from '../table';
import intentStyles from './IntentDetails.module.scss';
import styles from './ImportFromCsv.module.scss';
import { OnChangeUtterance } from '../intent/UtteranceList';
import { useServices } from '../../hooks';

interface ImportFromCsvProps {
  isOpen: boolean;
  importUtterances: OnChangeUtterance;
  onClickBack: () => void;
}

interface DataImport {
  checked: boolean;
  utterances: string;
}

interface UtteranceCSV {
  utterances: string;
}

const exampleCsv = createCsv<UtteranceCSV>({
  columns: [
    {
      label: 'utterances',
      getter: 'utterances'
    }
  ],
  items: [
    { utterances: 'Phrase 1' },
    { utterances: 'Phrase 2' }
  ]
});

const InputCheckbox: FC<{
  checked: boolean;
  index: number;
  onClick: (checked: boolean, index: number) => void;
}> = (props) => {
  return (
    <input
      type="checkbox"
      defaultChecked={props.checked}
      onClick={(e) => {
        props.onClick(e.currentTarget.checked, props.index);
      }}
    />
  );
};

export const ImportFromCsv: FC<ImportFromCsvProps> = ({
  importUtterances,
  isOpen,
  onClickBack
}) => {
  const { errorMessageService } = useServices();
  const [importedData, setImportedData] = useState<DataImport[]>([]);
  const [isDataParsed, setIsDataParsed] = useState<boolean>(false);
  const [utteranceToEdit, setUtteranceToEdit] = useState<number>(-1);
  const selectedData = importedData.filter(({ checked }) => {
    return checked;
  });
  const isAllSelected = importedData.length === selectedData.length;

  if (!isOpen) {
    return null;
  }

  const onParse = (text: string) => {
    try {
      const rows = Papa.parse<UtteranceCSV>(
        text,
        { header: true }
      ).data.map((row) => {
        return row.utterances?.trim();
      }).filter((utterances) => {
        return utterances.length;
      });
      if (!rows.length) {
        throw new Error('Unexpected CSV. Check Your Data And Try again.');
      }

      setImportedData(rows.map((utterances) => {
        return {
          checked: false,
          utterances
        };
      }));
      setIsDataParsed(true);
    } catch (err) {
      window.apm.notification.post('error', err);
      setIsDataParsed(false);
    }
  };

  const onClickContinueBtn = () => {
    setIsDataParsed(true);
  };

  const onClickSelectAllBtn = () => {
    setImportedData(importedData.map((row) => {
      return {
        ...row,
        checked: !isAllSelected
      };
    }));
  };

  const onClickCheckbox = (checked: boolean, indexArr: number) => {
    setImportedData(importedData.map((row, index) => {
      return (index === indexArr) ? { ...row, checked } : row;
    }));
  };

  const onClickImportBtn = () => {
    if (!selectedData.length) {
      errorMessageService.addError({
        message: 'Please select items to import.',
        title: 'Import Error'
      });
      return;
    }

    selectedData.forEach(({ utterances }) => {
      importUtterances({
        type: 'add',
        payload: {
          value: utterances
        }
      });
    });
    onClickBack();
  };

  const onClickEditBtn = (utteranceRowIndex: number) => {
    setUtteranceToEdit(utteranceRowIndex);
  };

  const onKeyDownUtteranceTxt = (e: KeyboardEvent<HTMLInputElement>, index: number) => {
    if (e.keyCode === 13) {
      const _importedData = _.cloneDeep(importedData);
      _importedData[index].utterances = e.currentTarget.value;

      setImportedData(_importedData);
      setUtteranceToEdit(-1);
    } else if (e.keyCode === 27) {
      setUtteranceToEdit(-1);
    }
  };

  return (
    <Box
      className={`${intentStyles.box} slide-in`}
      style={{ '--slide-in-width': '100%' } as any}
      theme="noPadding"
    >
      <div className={intentStyles.header}>
        <h2 className={intentStyles.title}>Import From CSV</h2>
        {(!isDataParsed) ? null : (
          <Button
            type="button"
            theme="default"
            onClick={onClickSelectAllBtn}
          >
            {isAllSelected ? 'Unselect all' : 'Select all'}
          </Button>
        )}
      </div>
      <div className={`${intentStyles.content} ${intentStyles.flex}`}>
        {
          isDataParsed ? (
            <>
              <Table
                className={styles.table}
                keyAsId='id'
                data={importedData.map(({
                  checked,
                  utterances
                }, index) => {
                  // This has to be regenerated to get the checkbox to rerender properly.
                  const id = _.uniqueId();
                  return {
                    id,
                    '': <InputCheckbox checked={checked} index={index} onClick={onClickCheckbox} />,
                    utterances: utteranceToEdit === index ? (
                      <input type="text" className={styles.inputText} defaultValue={utterances} onKeyDown={(e) => onKeyDownUtteranceTxt(e, index)} onBlur={() => setUtteranceToEdit(-1)} autoFocus />
                    ) : utterances,
                    actions: <div className={styles.svgButtonGroup}>
                      <button key={`${id}-edit`} className={styles.svgButton} onClick={() => onClickEditBtn(index)}>
                        <i className="fe icon-edit" />
                      </button>
                    </div>
                  };
                })}
                sortByColumns={['utterances']}
              />
            </>
          ) : (
            <div className={styles.importWrapper}>
              <FileUploader
                template={{
                  fileName: 'utterances.csv',
                  text: exampleCsv
                }}
                onParse={onParse}
              />
            </div>
          )
        }
      </div>
      <div className={intentStyles.footer}>
        {
          isDataParsed && (
            <Button
              onClick={() => {
                setIsDataParsed(false);
              }}
              theme="outline"
              type="button"
            >
              Back to Import
            </Button>
          )
        }
        <div className={intentStyles.buttonGroup}>
          <Button type="button" theme="outline" onClick={onClickBack}>
            Back to Intent
          </Button>
          {
            isDataParsed
              ? (
                <Button
                  type="button"
                  theme="default"
                  onClick={onClickImportBtn}
                >
                  Import
                </Button>
              ) : (
                <Button
                  type="button"
                  theme="outlineHighlight"
                  onClick={onClickContinueBtn}
                >
                  Continue
                </Button>
              )}
        </div>
      </div>
    </Box>
  );
};

